import { isGenericFormField } from "../../../utils/genericFieldSupport";
import { splitPersonName } from "../../../utils/personName";

const OWNER_NAME_PART_FIELD_KINDS = new Map([
  ["0a191f37-9af0-482e-9e30-7e1b6efec193", "firstName"],
  ["65006160-fcc4-45d4-a5c3-d5d8139311ed", "lastName"],
]);

const isBlankValue = (value) =>
  value === undefined || value === null || value === "";

const normalizeOwnerFieldMetadata = (...values) =>
  values
    .filter(Boolean)
    .map((value) => String(value).replace(/([a-z0-9])([A-Z])/g, "$1 $2"))
    .join(" ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const getOwnerDetailFieldKind = (field = {}) => {
  // Generic widget labels are display-only, not owner-data mappings.
  if (isGenericFormField(field)) return null;

  const namePartKind = OWNER_NAME_PART_FIELD_KINDS.get(field?.field_id);
  if (namePartKind) return namePartKind;

  const fieldName = String(field?.name || field?.field_name || "");
  const normalizedName = normalizeOwnerFieldMetadata(fieldName);
  const metadata = normalizeOwnerFieldMetadata(
    fieldName,
    field?.label,
    field?.field_label,
    field?.title,
  );

  const hasOwnerContext =
    /\b(?:property\s+|home\s+)?owner(?:ship)?\b/.test(metadata) ||
    /^o\s+(?:address|street|city|locality|place|state|province|zip|postal|phone|mobile|cell|email)\b/.test(
      normalizedName,
    );

  if (!hasOwnerContext) return null;

  const isSecondaryAddressField =
    /\b(?:address|street)(?:\s+line)?\s*(?:2|unit|suite|apt|apartment)\b/.test(
      metadata,
    );
  if (isSecondaryAddressField) return null;

  const detailPatterns = [
    ["email", /\b(?:email|e\s+mail)\b/],
    ["phone", /\b(?:phone|mobile|cell|telephone)\b/],
    ["city", /\b(?:city|locality|place(?:\s+name)?)\b/],
    ["state", /\b(?:state|province)\b/],
    ["zip", /\b(?:zip(?:\s+code)?|postal(?:\s+code)?|postcode)\b/],
  ];
  const detailKind = detailPatterns.find(([, pattern]) =>
    pattern.test(metadata),
  )?.[0];

  if (detailKind) return detailKind;
  if (/\bstreet\b/.test(normalizedName)) return "street";
  if (/\b(?:address|street)(?:\s+line)?\b/.test(metadata)) return "address";

  return null;
};

export const applyOwnerDetailPrefills = ({
  updatedValues,
  fields = [],
  defaultFormFields = [],
  selectedRow,
}) => {
  if (!updatedValues || !Array.isArray(fields)) return updatedValues;

  const propertyOwner = selectedRow?.property_owner || {};
  const addressParts = propertyOwner?.address_parts || {};
  const ownerNameParts = splitPersonName(
    propertyOwner?.personalName || propertyOwner?.name,
  );
  const detailValues = {
    firstName: ownerNameParts.firstName,
    lastName: ownerNameParts.lastName,
    street: addressParts?.address1,
    address:
      propertyOwner?.address || addressParts?.address || addressParts?.address1,
    city:
      addressParts?.city ||
      propertyOwner?.city ||
      selectedRow?.chunked_addresses?.owner_address?.place_name,
    state: addressParts?.state || propertyOwner?.state,
    zip:
      addressParts?.zip_code ||
      addressParts?.zipCode ||
      propertyOwner?.zip ||
      propertyOwner?.postalCode,
    phone:
      propertyOwner?.mobile ||
      propertyOwner?.phone ||
      propertyOwner?.phone_name,
    email: propertyOwner?.email,
  };
  const ownerDetailKindByFieldId = new Map();
  const fieldsByName = new Map();

  (Array.isArray(defaultFormFields) ? defaultFormFields : []).forEach(
    (field) => {
      const detailKind = getOwnerDetailFieldKind(field);

      if (field?.field_id && detailKind) {
        ownerDetailKindByFieldId.set(field.field_id, detailKind);
      }
    },
  );

  fields.forEach((field) => {
    // Retain generic metadata so the name-only fallback cannot reclassify it.
    if (field?.name) fieldsByName.set(field.name, field);
  });
  Object.keys(updatedValues).forEach((fieldName) => {
    if (!fieldsByName.has(fieldName)) {
      fieldsByName.set(fieldName, { name: fieldName });
    }
  });

  fieldsByName.forEach((field, fieldName) => {
    // Generic targets use their explicit instance-based prefill configuration.
    // Skip before the catalog lookup as well, without clearing existing values.
    if (isGenericFormField(field)) return;

    const detailKind =
      ownerDetailKindByFieldId.get(field?.field_id) ||
      getOwnerDetailFieldKind(field);
    const detailValue = detailValues[detailKind];

    if (
      detailKind &&
      isBlankValue(updatedValues[fieldName]) &&
      !isBlankValue(detailValue)
    ) {
      updatedValues[fieldName] = detailValue;
    }
  });

  return updatedValues;
};
